import React, { useMemo, useState } from 'react';
import { Table, Spin, Empty, Select } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import PageLayout from '@components/layout/PageLayout';
import { useAuditSummaryData } from '@hooks/graphql/useAuditSummaryData';
import styles from './AuditSummary.module.css';

interface AuditSummaryRow {
  category: string;
  occurrences: number;
}

const WINDOW_OPTIONS = [
  { value: 12, unit: 'hour', label: 'Last 12 Hours' },
  { value: 24, unit: 'hour', label: 'Last 24 Hours' },
  { value: 3, unit: 'day', label: 'Last 3 Days' },
  { value: 5, unit: 'day', label: 'Last 5 Days' },
  { value: 7, unit: 'day', label: 'Last 7 Days' },
  { value: 30, unit: 'day', label: 'Last 30 Days' },
] as const;

const columns: ColumnsType<AuditSummaryRow> = [
  {
    title: 'Category',
    dataIndex: 'category',
    key: 'category',
    width: '50%',
    align: 'center',
    sorter: (a, b) => a.category.localeCompare(b.category),
  },
  {
    title: 'Occurrences',
    dataIndex: 'occurrences',
    key: 'occurrences',
    width: '50%',
    align: 'center',
    defaultSortOrder: 'descend',
    sorter: (a, b) => a.occurrences - b.occurrences,
  },
];

const AuditSummary = () => {
  const [windowKey, setWindowKey] = useState<number>(0);
  const selectedWindow = WINDOW_OPTIONS[windowKey];

  const startDate = useMemo(
    () => dayjs().subtract(selectedWindow.value, selectedWindow.unit).toISOString(),
    [selectedWindow],
  );

  const { data, loading } = useAuditSummaryData(startDate);

  return (
    <PageLayout
      title="Audit Summary"
      headerAccessoryView={(
        <Select
          size="small"
          value={windowKey}
          onChange={setWindowKey}
          options={WINDOW_OPTIONS.map((opt, idx) => ({ value: idx, label: opt.label }))}
          style={{ width: 150 }}
        />
      )}
    >
      <div className={styles.tableWrapper}>
        <Spin spinning={loading}>
          <Table
            columns={columns}
            dataSource={data}
            rowKey="category"
            pagination={false}
            size="middle"
            locale={{ emptyText: <Empty description={`No audit activity in the ${selectedWindow.label.toLowerCase()}`} /> }}
          />
        </Spin>
      </div>
    </PageLayout>
  );
};

export default AuditSummary;
